import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { NoteMarkdown } from '@/components/NoteMarkdown'
import { ZoomableImage, ZoomImage } from '@/components/ui/ZoomImage'

export interface PhotoGalleryProps {
  /** The group's photo URLs, in display order. */
  photoUrls: string[]
  /** The single caption the whole group shares. */
  caption?: string
  /** Index of the photo the viewer opened on. */
  startIndex: number
  onClose: () => void
}

/**
 * Fullscreen viewer for a photo-note group: one photo at a time, previous/next
 * arrows (and ← → keys), a position counter, and the group's shared caption.
 * Each photo gets the shared zoom/pan/rotate viewer; flipping to another
 * photo starts it fresh (the viewer is keyed by URL).
 */
export function PhotoGallery(props: PhotoGalleryProps) {
  const { photoUrls, caption, startIndex, onClose } = props
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(startIndex, 0), photoUrls.length - 1),
  )

  const showPrevious = () =>
    setIndex((current) => (current - 1 + photoUrls.length) % photoUrls.length)
  const showNext = () => setIndex((current) => (current + 1) % photoUrls.length)

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') showPrevious()
      if (event.key === 'ArrowRight') showNext()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navButtonClass =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25'

  // Portal to <body>: hosts (side panels, sticky tables) create their own
  // stacking contexts, which would trap the overlay's z-index.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-black/90 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
    >
      <div
        className="flex w-full max-w-[95vw] items-center justify-center gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        {photoUrls.length > 1 ? (
          <button
            type="button"
            className={navButtonClass}
            aria-label="Previous photo"
            onClick={showPrevious}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}

        <ZoomableImage
          key={photoUrls[index]}
          src={photoUrls[index]}
          alt={caption || `Photo ${index + 1} of ${photoUrls.length}`}
          onClose={onClose}
          imgClassName="max-h-[78vh] min-w-0 max-w-full"
        />

        {photoUrls.length > 1 ? (
          <button
            type="button"
            className={navButtonClass}
            aria-label="Next photo"
            onClick={showNext}
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div
        className="flex max-w-[85vw] flex-col items-center gap-1 text-center"
        onClick={(event) => event.stopPropagation()}
      >
        {photoUrls.length > 1 ? (
          <span className="text-xs text-white/70">
            {index + 1} / {photoUrls.length}
          </span>
        ) : null}
        {caption ? (
          <NoteMarkdown
            text={caption}
            className="max-h-[10vh] overflow-y-auto text-[12.5px] leading-normal text-white/90"
          />
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

export interface NotePhotosProps {
  /** All current photos of the note, display order. Empty renders nothing. */
  photoUrls: string[]
  /** Group caption, used for alt text and shown inside the gallery. */
  caption?: string
  /** When false, thumbnails render inert (no preview/gallery). */
  enablePreview?: boolean
}

/**
 * A note's photo area: a single photo keeps the existing zoomable image; a
 * group renders a thumbnail grid that opens the {@link PhotoGallery} on the
 * tapped photo.
 */
export function NotePhotos(props: NotePhotosProps) {
  const { photoUrls, caption, enablePreview = true } = props
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null)

  if (photoUrls.length === 0) return null

  if (photoUrls.length === 1) {
    return (
      <div className="w-fit max-w-full overflow-hidden rounded-md border border-[#e7e2d5] bg-slate-100">
        <ZoomImage
          src={photoUrls[0]}
          alt={caption || 'Work photo'}
          className="max-h-[200px] w-auto max-w-full object-contain"
          disabled={!enablePreview}
        />
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {photoUrls.map((url, index) => (
          <button
            key={`${url}-${index}`}
            type="button"
            className={`overflow-hidden rounded-md border border-[#e7e2d5] bg-slate-100 ${
              enablePreview ? 'cursor-zoom-in' : 'cursor-default'
            }`}
            onClick={(event) => {
              if (!enablePreview) return
              event.stopPropagation()
              setGalleryIndex(index)
            }}
            aria-label={`Open photo ${index + 1} of ${photoUrls.length}`}
          >
            {/* data-zoom-image keeps hosts that special-case image taps
                (e.g. the invoice checklist rows) from toggling on it. */}
            <img
              src={url}
              alt={caption || `Photo ${index + 1}`}
              data-zoom-image="true"
              className="h-24 w-24 object-cover"
            />
          </button>
        ))}
      </div>
      {galleryIndex !== null ? (
        <PhotoGallery
          photoUrls={photoUrls}
          caption={caption}
          startIndex={galleryIndex}
          onClose={() => setGalleryIndex(null)}
        />
      ) : null}
    </>
  )
}
